import { TableCellTypes, Radio } from '@carboledger/ui';
import {
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeAssignQuantityCell,
  makeDiscrepanciesCell,
  makeRemoveCell,
} from '../tableCells';
import { orderUnits } from './linkUnits';

// Link orders wizard — step 1 picks the orders, step 2 gives each one a quantity.

const renderUnitType = makeUnitTypeCell((t, row) => orderUnits.buildRowUnitOptions(t, row.byUnit));

const textColumn = (id, header, width = 200) => ({
  id,
  header,
  type: TableCellTypes.text,
  width,
  sortable: true,
});

// Width is per-column so a long header renders in full rather than truncating.
const quantityColumn = (id, header, width = 200) => ({
  id,
  header,
  type: TableCellTypes.custom,
  width,
  sortable: true,
  cell: makeUnitScopedCell(id),
});

const unitTypeColumn = (t, headerSelect) => ({
  id: 'unitType',
  header: t('FIELD_UNIT_TYPE', { unit: orderUnits.unitTypeLabel(t, headerSelect.value) }),
  type: TableCellTypes.custom,
  width: 252,
  sortable: true,
  cell: renderUnitType,
  headerSelect,
});

/**
 * `selectMode` is 'multi' (the Table's own checkbox column) or 'single', which needs an
 * app-owned radio column instead — the bulk flows link one row to the whole selection.
 * TODO: name the control once the library Radio takes an aria-label.
 */
const withPicker = (columns, { selectMode, selectedId, onSelect, name }) => {
  if (selectMode !== 'single') return columns;
  const RadioCell = ({ row }) => (
    <Radio
      name={name}
      value={String(row.id)}
      checked={selectedId === row.id}
      onChange={() => onSelect(row.id)}
    />
  );
  return [
    {
      id: '__pick__',
      header: '',
      type: TableCellTypes.custom,
      width: 56,
      resizable: false,
      reorderable: false,
      cell: (ctx) => <RadioCell row={ctx.row} />,
    },
    ...columns,
  ];
};

// Column order follows the design exactly; there is no Status or Tolerance column here.
export const buildLinkOrdersStep1Columns = (
  t,
  { headerSelect, onOpenDiscrepancy, selectMode, selectedId, onSelect },
) =>
  withPicker(
    [
      textColumn('orderNo', t('ORDERS_COL_ORDER_NO'), 140),
      textColumn('totalShipmentsLinked', t('LINK_ORDERS_COL_TOTAL_SHIPMENTS_LINKED'), 260),
      unitTypeColumn(t, headerSelect),
      quantityColumn('unassignedQuantity', t('FIELD_UNASSIGNED_QUANTITY'), 210),
      quantityColumn('contractedQuantity', t('FIELD_CONTRACTED_QUANTITY'), 210),
      quantityColumn('shippedQuantity', t('FIELD_SHIPPED_QUANTITY')),
      quantityColumn('remainingToShipQuantity', t('ORDERS_COL_REMAINING_TO_SHIP'), 250),
      quantityColumn('allocatedQuantity', t('FIELD_ALLOCATED_QUANTITY')),
      quantityColumn('remainingToAllocateQuantity', t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'), 280),
      textColumn('ghgEmissions', t('FIELD_GHG_EMISSIONS')),
      textColumn('ghgSavings', t('LINK_SHIPMENTS_COL_GHG_SAVINGS')),
      textColumn('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE')),
      textColumn('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN')),
      textColumn('feedstockCategory', t('FIELD_FEEDSTOCK_CATEGORY')),
      textColumn('allocationPreference', t('FIELD_ALLOCATION_PREFERENCE'), 220),
      textColumn('dispatchBy', t('FIELD_DATE_OF_DISPATCH'), 150),
      discrepanciesColumn(t, onOpenDiscrepancy),
    ],
    { selectMode, selectedId, onSelect, name: 'linkOrder' },
  );

/**
 * Step 1 from an ORDER — the outgoing shipments to pick from (Figma 4196:113122).
 * Deliberately short: the design carries no sustainability columns on this side.
 */
export const buildLinkShipmentsStep1Columns = (
  t,
  { headerSelect, onOpenDiscrepancy, selectMode, selectedId, onSelect },
) =>
  withPicker(
    [
      textColumn('shipmentId', t('FIELD_SHIPMENT_ID'), 160),
      textColumn('linkedOrders', t('FIELD_TOTAL_ORDER_LINKED'), 200),
      unitTypeColumn(t, headerSelect),
      quantityColumn('unassignedQuantity', t('FIELD_UNASSIGNED_QUANTITY'), 210),
      quantityColumn('shipmentQuantity', t('FIELD_SHIPMENT_QUANTITY'), 210),
      quantityColumn(
        'remainingToAllocateQuantity',
        t('OUTGOING_SHIPMENTS_COL_REMAINING_TO_ALLOCATE'),
        250,
      ),
      textColumn('dateOfDispatch', t('FIELD_DATE_OF_DISPATCH'), 150),
      discrepanciesColumn(t, onOpenDiscrepancy),
    ],
    { selectMode, selectedId, onSelect, name: 'linkOutgoingShipment' },
  );

const assignColumn = (t, assignments, onAssign) => ({
  id: 'assignedQuantity',
  header: t('ORDERS_COL_ASSIGN_QUANTITY'),
  type: TableCellTypes.custom,
  width: 260,
  cell: makeAssignQuantityCell(assignments, onAssign),
});

// Every step-2 frame closes on Remove, which drops the row from the save.
const removeColumn = (t, onRemove) => ({
  id: '__remove__',
  header: t('FIELD_ACTION'),
  type: TableCellTypes.custom,
  width: 120,
  resizable: false,
  cell: makeRemoveCell(onRemove),
});

const discrepanciesColumn = (t, onOpenDiscrepancy) => ({
  id: 'discrepancies',
  header: t('FIELD_DISCREPANCIES'),
  type: TableCellTypes.custom,
  width: 200,
  sortable: true,
  cell: makeDiscrepanciesCell(onOpenDiscrepancy),
});

/**
 * Step 2's order table — step 1's columns with Assign quantity third (Figma 4196:123484 and
 * 4196:113122 draw the same 18).
 */
const orderAssignColumns = (
  t,
  { headerSelect, assignments, onAssign, onRemove, onOpenDiscrepancy },
) => [
  textColumn('orderNo', t('ORDERS_COL_ORDER_NO'), 140),
  textColumn('totalShipmentsLinked', t('LINK_ORDERS_COL_TOTAL_SHIPMENTS_LINKED'), 260),
  assignColumn(t, assignments, onAssign),
  unitTypeColumn(t, headerSelect),
  quantityColumn('unassignedQuantity', t('FIELD_UNASSIGNED_QUANTITY'), 210),
  quantityColumn('contractedQuantity', t('FIELD_CONTRACTED_QUANTITY'), 210),
  quantityColumn('shippedQuantity', t('FIELD_SHIPPED_QUANTITY')),
  quantityColumn('remainingToShipQuantity', t('ORDERS_COL_REMAINING_TO_SHIP'), 250),
  quantityColumn('allocatedQuantity', t('FIELD_ALLOCATED_QUANTITY')),
  quantityColumn('remainingToAllocateQuantity', t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'), 280),
  textColumn('ghgEmissions', t('FIELD_GHG_EMISSIONS')),
  textColumn('ghgSavings', t('LINK_SHIPMENTS_COL_GHG_SAVINGS')),
  textColumn('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE')),
  textColumn('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN')),
  textColumn('feedstockCategory', t('FIELD_FEEDSTOCK_CATEGORY')),
  textColumn('allocationPreference', t('FIELD_ALLOCATION_PREFERENCE'), 220),
  textColumn('dispatchBy', t('FIELD_DATE_OF_DISPATCH'), 150),
  discrepanciesColumn(t, onOpenDiscrepancy),
  removeColumn(t, onRemove),
];

/** Step 2's ORDER table — the picked candidates, or a bulk flow's ticked orders. */
export const buildLinkOrdersStep2Columns = orderAssignColumns;

/** Bulk step 2 from a SHIPMENT selection — one table of those shipments (Figma 4196:122493). */
export const buildLinkShipmentsStep2Columns = (
  t,
  { headerSelect, assignments, onAssign, onRemove, onOpenDiscrepancy },
) => [
  textColumn('shipmentId', t('FIELD_SHIPMENT_ID'), 160),
  textColumn('linkedOrders', t('FIELD_TOTAL_ORDER_LINKED'), 200),
  assignColumn(t, assignments, onAssign),
  unitTypeColumn(t, headerSelect),
  quantityColumn('unassignedQuantity', t('FIELD_UNASSIGNED_QUANTITY'), 210),
  quantityColumn('shipmentQuantity', t('FIELD_SHIPMENT_QUANTITY'), 210),
  quantityColumn('allocatedQuantity', t('FIELD_ALLOCATED_QUANTITY')),
  quantityColumn('remainingToAllocateQuantity', t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'), 280),
  textColumn('dateOfDispatch', t('FIELD_DATE_OF_DISPATCH'), 150),
  discrepanciesColumn(t, onOpenDiscrepancy),
  removeColumn(t, onRemove),
];

/**
 * Step 2 from ONE order — the picked shipments. Assign sits SECOND here, not third:
 * 4196:111461 is the one frame that puts it there, and this is the only flow it draws.
 */
export const buildLinkShipmentsAssignColumns = (
  t,
  { headerSelect, assignments, onAssign, onRemove, onOpenDiscrepancy },
) => [
  textColumn('shipmentId', t('FIELD_SHIPMENT_ID'), 160),
  assignColumn(t, assignments, onAssign),
  textColumn('linkedOrders', t('FIELD_TOTAL_ORDER_LINKED'), 200),
  unitTypeColumn(t, headerSelect),
  quantityColumn('unassignedQuantity', t('FIELD_UNASSIGNED_QUANTITY'), 210),
  quantityColumn('shipmentQuantity', t('FIELD_SHIPMENT_QUANTITY'), 210),
  quantityColumn(
    'remainingToAllocateQuantity',
    t('OUTGOING_SHIPMENTS_COL_REMAINING_TO_ALLOCATE'),
    250,
  ),
  textColumn('ghgEmissions', t('FIELD_GHG_EMISSIONS')),
  discrepanciesColumn(t, onOpenDiscrepancy),
  removeColumn(t, onRemove),
];
